import * as Sentry from '@sentry/react';
import { Button, Typography } from '@eye-able/ui';
import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { RouterLink } from '@/components/RouterLink';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import ErrorFeedbackForm from './ErrorFeedbackForm';
import NotFoundPage from './NotFoundPage';

type RouteError = {
  message?: string;
  status?: number;
  statusText?: string;
};

function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const e = error as Partial<RouteError>;

    if (e.statusText) {
      return e.statusText;
    }

    if (e.message) {
      return e.message;
    }
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export default function ErrorPage({ onRetry }: { onRetry?: () => void } = {}) {
  const { t } = useInlineTranslation();
  const error = useRouteError() as RouteError | null | undefined;

  useEffect(() => {
    if (error?.status === 404 || error?.status === 403) return;

    Sentry.captureException(error);
  }, [error]);

  if (error?.status === 404) {
    return <NotFoundPage is404 />;
  }

  const message = getErrorMessage(error ?? {});

  const versionUpdated = message.includes(
    'Failed to fetch dynamically imported module',
  );
  const forbidden = error?.status === 403;

  return (
    <div className='eye:flex eye:flex-col eye:gap-xl eye:p-xl'>
      <Typography data-test-id='error-page' variant='headline-l'>
        <T
          k={
            forbidden
              ? 'noAccess'
              : versionUpdated
                ? 'versionOutdated'
                : 'somethingWentWrong'
          }
        />
      </Typography>

      <div className='eye:flex eye:flex-wrap eye:gap-2xs'>
        <Button
          onClick={
            versionUpdated
              ? () => window.location.reload()
              : (onRetry ?? (() => window.location.reload()))
          }
        >
          {t('refreshPage')}
        </Button>
        <Button asChild variant='outline'>
          <RouterLink to='/'>{t('backToHome')}</RouterLink>
        </Button>
      </div>

      {!versionUpdated && !forbidden && <ErrorFeedbackForm />}
    </div>
  );
}
